import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook para manejar setTimeout con tiempo dinámico al llamar la función.
 * @param callback - La función que se ejecutará después del tiempo especificado.
 * @returns start - Función para iniciar el temporizador con un tiempo dinámico.
 * @returns clear - Función para cancelar el temporizador.
 */
function useTimeout(callback: () => void) {
	const callbackRef = useRef(callback);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	// Mantiene la referencia actualizada al callback
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	/**
	 * Inicia el temporizador con un tiempo dinámico.
	 * @param delay - Tiempo en milisegundos antes de ejecutar el callback.
	 */
	const start = useCallback((delay: number) => {
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => callbackRef.current(), delay);
	}, []);

	/**
	 * Cancela el temporizador si está activo.
	 */
	const clear = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	// Limpieza automática cuando el componente se desmonta
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	return { start, clear };
}

export default useTimeout;
